import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { formatCOP } from '@/utils/format';
import { resolveImageUrl } from '@/utils/images';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';

const CartSidebar = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, subtotal, itemCount } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-[#FDFBF7] flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b border-[#E7E5E4]">
          <SheetTitle className="font-display flex items-center gap-2 text-[#1C1917]" data-testid="cart-title">
            <ShoppingBag className="w-5 h-5" />
            Tu carrito {itemCount > 0 && <span className="text-sm text-[#8BA58F]">({itemCount})</span>}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-20 h-20 rounded-full bg-[#F7F2E8] flex items-center justify-center mb-4">
              <ShoppingBag className="w-10 h-10 text-[#8BA58F]" />
            </div>
            <p className="font-display text-lg text-[#1C1917] mb-2">Tu carrito está vacío</p>
            <p className="text-sm text-[#57534E] mb-6">Explora nuestro catálogo y encuentra el alimento ideal</p>
            <Button
              onClick={() => {
                setIsOpen(false);
                navigate('/perros');
              }}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6"
              data-testid="cart-empty-cta"
            >
              Comprar ahora
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.variant_id} className="flex gap-4 bg-white rounded-xl p-4 border border-[#E7E5E4]" data-testid={`cart-item-${item.variant_id}`}>
                    <div className="w-20 h-20 rounded-lg bg-[#F7F2E8] shrink-0 overflow-hidden">
                      {item.image && <img src={resolveImageUrl(item.image, 200)} alt={item.product_name} className="w-full h-full object-cover" loading="lazy" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-[#8BA58F] font-semibold">{item.brand}</p>
                      <p className="text-sm font-semibold text-[#1C1917] line-clamp-2 leading-tight">{item.product_name}</p>
                      <p className="text-xs text-[#57534E] mt-1">{item.size}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 border border-[#E7E5E4] rounded-full">
                          <button
                            onClick={() => updateQuantity(item.variant_id, item.quantity - 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#F7F2E8]"
                            data-testid={`cart-decrease-${item.variant_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-medium w-5 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.variant_id, item.quantity + 1)}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#F7F2E8]"
                            data-testid={`cart-increase-${item.variant_id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <p className="text-sm font-bold text-[#2A5C43]">{formatCOP(item.price * item.quantity)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.variant_id)}
                      className="text-[#57534E] hover:text-red-600 self-start"
                      data-testid={`cart-remove-${item.variant_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#E7E5E4] px-6 py-5 bg-white">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#57534E]">Subtotal</span>
                <span className="font-display text-xl font-bold text-[#2A5C43]" data-testid="cart-subtotal">{formatCOP(subtotal)}</span>
              </div>
              <p className="text-[11px] text-[#57534E] mb-4">IVA incluido • Envío calculado al finalizar</p>
              <Button
                onClick={handleCheckout}
                className="w-full bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-6 font-semibold"
                data-testid="cart-checkout-button"
              >
                Finalizar compra
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsOpen(false)}
                className="w-full mt-2 text-[#57534E]"
                data-testid="cart-continue-shopping"
              >
                Continuar comprando
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
